import { useRef, useState } from 'react';
import { useNotifier } from '../notifier';

export const PDF_OPEN_AND_DOWNLOAD_TOGGLE = 'EnablePdfOpenAndDownloadActions';

const pdfContentType = 'application/pdf';

// Revoking the URL right away races the new tab's navigation and leaves it blank.
const revokeObjectUrlDelayInMs = 60_000;

export type FetchedFile = Blob | { data: Blob; fileName?: string };

export function isPdfFileName(fileName?: string) {
  return !!fileName && fileName.toLowerCase().endsWith('.pdf');
}

export function isPdfFile(contentType?: string, fileName?: string) {
  return contentType ? contentType.toLowerCase() === pdfContentType : isPdfFileName(fileName);
}

function toFile(fetched: FetchedFile) {
  return fetched instanceof Blob ? { data: fetched, fileName: undefined } : fetched;
}

function saveFile(data: Blob, fileName: string) {
  const fileUrl = URL.createObjectURL(data);
  const link = document.createElement('a');
  link.href = fileUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(fileUrl), revokeObjectUrlDelayInMs);
}

export function usePdfActions() {
  const { dispatch: dispatchNotifier } = useNotifier();
  const [isFetching, setIsFetching] = useState(false);
  const isFetchingRef = useRef(false);

  function notify(title: string) {
    dispatchNotifier({ type: 'notify', payload: { title, severity: 'warning' } });
  }

  async function fetchOnce(fetchFile: () => Promise<FetchedFile>) {
    isFetchingRef.current = true;
    setIsFetching(true);
    try {
      return toFile(await fetchFile());
    } finally {
      isFetchingRef.current = false;
      setIsFetching(false);
    }
  }

  async function openPdf(fetchFile: () => Promise<FetchedFile>, fileNameIfNotPdf?: string) {
    if (isFetchingRef.current) {
      return;
    }
    // The tab must be opened within the click, as browsers only allow user-initiated tabs.
    const pdfTab = window.open('', '_blank');
    if (!pdfTab) {
      notify('Browseren blokerede den nye fane. Tillad pop op-vinduer for Fasit, eller download PDF’en i stedet.');
      return;
    }

    try {
      const { data, fileName } = await fetchOnce(fetchFile);
      if (fileNameIfNotPdf && data.type && data.type !== pdfContentType) {
        pdfTab.close();
        saveFile(data, fileName ?? fileNameIfNotPdf);
        return;
      }
      const pdfUrl = URL.createObjectURL(new Blob([data], { type: pdfContentType }));
      pdfTab.location.href = pdfUrl;
      window.setTimeout(() => URL.revokeObjectURL(pdfUrl), revokeObjectUrlDelayInMs);
    } catch {
      pdfTab.close();
      notify('PDF’en kunne ikke hentes.');
    }
  }

  async function downloadFile(fetchFile: () => Promise<FetchedFile>, fallbackFileName: string) {
    if (isFetchingRef.current) {
      return;
    }

    try {
      const { data, fileName } = await fetchOnce(fetchFile);
      saveFile(data, fileName || fallbackFileName);
    } catch {
      notify('Filen kunne ikke hentes.');
    }
  }

  return { isFetching, openPdf, downloadFile };
}
